import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';

import { supabase } from '../lib/supabase';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    let lastUid: string | null = null;
    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Ignora renovação de token quando o usuário é o mesmo: trocar o objeto de
      // sessão a cada foco da aba causava re-fetch e o "recarregar sozinho".
      setSession((prev) => {
        if (event === 'TOKEN_REFRESHED' && prev?.user?.id === nextSession?.user?.id) return prev;
        return nextSession;
      });
      const uid = nextSession?.user?.id ?? null;
      if (event === 'SIGNED_IN' && uid && uid !== lastUid) {
        lastUid = uid;
        void supabase.rpc('accept_pending_invites');
      }
      if (event === 'SIGNED_OUT') lastUid = null;
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
